/**
 * Active-sessions list — powered by the multiSession plugin. Lets the
 * user see all signed-in sessions across devices and revoke any of them
 * (or all "other" sessions in one shot).
 *
 * IMPORTANT: /revoke-session takes the session TOKEN, not the session
 * ID — see node_modules/better-auth/dist/api/routes/session.mjs:380.
 * The two fields are distinct; listSessions() returns both.
 */
import { useEffect, useState } from 'react';
import { Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/interfaces/learner/ui';
import { authClient } from '@/shared/auth/authClient';
import { SettingsSection } from './SettingsSection';

interface SessionEntry {
  id: string;
  /** Opaque token used as the revocation key. Distinct from session.id. */
  token: string;
  userAgent?: string | null;
  ipAddress?: string | null;
  // BetterAuth returns Date objects via the React client (Prisma's native
  // shape after JSON re-hydration). Accept both for safety.
  createdAt: Date | string;
  expiresAt: Date | string;
}

export default function SessionsSection() {
  const [sessions, setSessions] = useState<SessionEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    const { data, error } = await authClient.listSessions();
    if (error) {
      toast.error(error.message ?? 'Could not load sessions');
    } else {
      setSessions((data as SessionEntry[]) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const onRevoke = async (token: string) => {
    const { error } = await authClient.revokeSession({ token });
    if (error) {
      toast.error(error.message ?? 'Could not revoke session');
    } else {
      toast.success('Session revoked');
      refresh();
    }
  };

  const onRevokeAllOthers = async () => {
    const { error } = await authClient.revokeOtherSessions();
    if (error) {
      toast.error(error.message ?? 'Could not revoke sessions');
    } else {
      toast.success('All other sessions revoked');
      refresh();
    }
  };

  return (
    <SettingsSection title="Active sessions" delay={0.3} cardClassName="space-y-4">
      {loading && <p className="text-sm text-[var(--color-text-muted)]">Loading…</p>}
      {!loading && sessions.length === 0 && (
        <p className="text-sm text-[var(--color-text-muted)]">No active sessions.</p>
      )}
      {!loading && sessions.length > 0 && (
        <>
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between rounded-2xl border px-4 py-3"
                style={{
                  background: 'var(--color-surface-muted)',
                  borderColor: 'var(--color-surface-border)',
                }}
              >
                <div className="flex items-center gap-3">
                  <Monitor
                    size={18}
                    strokeWidth={1.75}
                    style={{ color: 'var(--color-text-muted)' }}
                  />
                  <div>
                    <p className="text-list-title">{s.userAgent ?? 'Unknown device'}</p>
                    <p className="text-meta-muted">
                      Started {new Date(s.createdAt).toLocaleString()}
                      {s.ipAddress ? ` · ${s.ipAddress}` : ''}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onPress={() => onRevoke(s.token)}>
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
          {sessions.length > 1 && (
            <Button variant="secondary" size="sm" onPress={onRevokeAllOthers}>
              Revoke all other sessions
            </Button>
          )}
        </>
      )}
    </SettingsSection>
  );
}
